import React, { useEffect, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { Alert, Spin, Tabs } from "antd";
import { useTranslation } from "react-i18next";
import { generalErrorAlert } from "../../../commons/GeneralErrorAlert";
import { loadMeta } from "./api";
import CatalogTab from "./CatalogTab";
import ImportTab from "./ImportTab";
import SuppliersTab from "./SuppliersTab";
import SettingsTab from "./SettingsTab";
import TextBlocksTab from "./TextBlocksTab";
import BasketsTab from "./BasketsTab";
import "./materials.css";

const TABS = ["catalog", "imports", "suppliers", "baskets", "texts", "settings"];

/**
 * /user/materials: the company's article catalog (own and wholesaler articles, labour items,
 * assemblies), DATANORM imports, suppliers with discounts and IDS shop access, wholesaler
 * baskets, text blocks and the calculation settings. Tabs are kept in ?tab=.
 */
const Materials = () => {
  const { t } = useTranslation("dashboard");
  const history = useHistory();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const tab = TABS.includes(params.get("tab")) ? params.get("tab") : "catalog";
  const [meta, setMeta] = useState(undefined);

  const refreshMeta = async () => {
    try {
      setMeta(await loadMeta({ refresh: true }));
    } catch (error) {
      generalErrorAlert(error);
      setMeta(null);
    }
  };

  useEffect(() => {
    refreshMeta();
  }, []);

  const setTab = key => {
    const next = new URLSearchParams(location.search);
    next.set("tab", key);
    if (key !== "baskets") next.delete("basket");
    history.replace(`${location.pathname}?${next.toString()}`);
  };

  if (meta === undefined) {
    return (
      <div className="materials-page materials-page--loading">
        <Spin />
      </div>
    );
  }
  if (meta === null) {
    return (
      <div className="materials-page">
        <Alert
          type="warning"
          showIcon
          message={t("You are not allowed to use the material catalog.")}
        />
      </div>
    );
  }

  const items = [
    { key: "catalog", label: t("Catalog"), children: <CatalogTab meta={meta} /> },
    {
      key: "imports",
      label: t("DATANORM import"),
      children: <ImportTab meta={meta} onImported={refreshMeta} />
    },
    {
      key: "suppliers",
      label: t("Suppliers & discounts"),
      children: <SuppliersTab meta={meta} onChanged={refreshMeta} />
    },
    {
      key: "baskets",
      label: t("Wholesaler baskets"),
      children: <BasketsTab meta={meta} highlight={params.get("basket")} />
    },
    { key: "texts", label: t("Text blocks"), children: <TextBlocksTab meta={meta} /> },
    {
      key: "settings",
      label: t("Calculation"),
      children: (
        <SettingsTab meta={meta} onSaved={settings => setMeta(prev => ({ ...prev, settings }))} />
      )
    }
  ];

  return (
    <div className="materials-page">
      <Tabs activeKey={tab} onChange={setTab} items={items} destroyInactiveTabPane />
    </div>
  );
};

export default Materials;
